import { useState, useCallback, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from './lib/routerCompat';
import type { AppRoutePath } from './lib/routes';
import './styles.css';
import { ToastContext } from './ToastContext';
import { useAuth } from './contexts/AuthContext';
import Footer from './components/Footer/Footer';
import InstallPrompt from './components/InstallPrompt';
import LazyImage from './components/LazyImage';
import pkg from '../package.json';
const { version } = pkg;

const TABS: { path: AppRoutePath; label: string }[] = [
  { path: '/feed', label: 'Discover' },
  { path: '/products', label: 'Marketplace' },
  { path: '/request', label: 'Campaign' },
  { path: '/messages', label: 'Messages' },
  { path: '/payments', label: 'Payments' },
  { path: '/stats', label: 'Analytics' },
  { path: '/subscribe', label: 'Subscribe' },
];

// Public pages render their own chrome (or none) instead of the app nav
const PUBLIC_PATHS: AppRoutePath[] = ['/', '/auth', '/privacy', '/terms'];

interface ToastState {
  visible: boolean;
  msg: string;
}

export default function App(): JSX.Element {
  const navigate = useNavigate();
  const { signOut, session, demoMode } = useAuth();
  const { location } = useRouterState();
  const isCreator = location.pathname === '/creator';
  // `pathname` is an arbitrary string; widen the typed path list for the membership test.
  const isPublic = (PUBLIC_PATHS as readonly string[]).includes(location.pathname);

  const [toast, setToast] = useState<ToastState>({ visible: false, msg: '' });
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ visible: true, msg });
    toastTimer.current = setTimeout(() => setToast({ visible: false, msg: '' }), 2800);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    []
  );

  return (
    <ToastContext.Provider value={showToast}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {!isPublic && (
          <nav className="nav">
            <Link to="/feed" className="nav-logo">
              <LazyImage
                src="/logo.png"
                alt="PitchVault"
                className="header-logo"
                width={56}
                height={56}
                eager
              />
              Pitch Vault
            </Link>

            <div className="nav-tabs">
              {TABS.map((t) => (
                <Link
                  key={t.path}
                  to={t.path}
                  className="nav-tab"
                  activeProps={{ className: 'nav-tab active' }}
                >
                  {t.label}
                </Link>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div className="role-toggle nav-role-toggle">
                <button
                  className={`role-btn${!isCreator ? ' active' : ''}`}
                  onClick={() => navigate({ to: '/feed' })}
                >
                  Brand
                </button>
                <button
                  className={`role-btn${isCreator ? ' active' : ''}`}
                  onClick={() => navigate({ to: '/creator' })}
                >
                  Creator
                </button>
              </div>

              <button
                className="nav-icon-btn"
                title="Messages"
                onClick={() => navigate({ to: '/messages' })}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </button>

              <button
                className="nav-hamburger"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Toggle menu"
              >
                {menuOpen ? '✕' : '☰'}
              </button>

              <div className="nav-profile-wrap">
                <button className="nav-profile-btn" onClick={() => setProfileOpen((o) => !o)}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </button>
                {profileOpen && (
                  <div className="nav-dropdown">
                    <div className="nav-dropdown-item" style={{ cursor: 'default', opacity: 0.7 }}>
                      {demoMode ? 'Demo account' : (session?.user?.email ?? 'Signed in')}
                    </div>
                    <div className="nav-dropdown-divider" />
                    <button
                      className="nav-dropdown-item danger"
                      onClick={async () => {
                        setProfileOpen(false);
                        await signOut();
                        navigate({ to: '/auth' });
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </nav>
        )}

        {!isPublic && menuOpen && (
          <div className="nav-mobile-menu">
            <div className="nav-mobile-role-section">
              <span className="nav-mobile-role-label">View As</span>
              <div className="nav-mobile-role-row">
                <button
                  className={`role-btn${!isCreator ? ' active' : ''}`}
                  onClick={() => {
                    navigate({ to: '/feed' });
                    setMenuOpen(false);
                  }}
                >
                  Brand
                </button>
                <button
                  className={`role-btn${isCreator ? ' active' : ''}`}
                  onClick={() => {
                    navigate({ to: '/creator' });
                    setMenuOpen(false);
                  }}
                >
                  Creator
                </button>
              </div>
            </div>
            {TABS.map((t) => (
              <Link
                key={t.path}
                to={t.path}
                className="nav-mobile-tab"
                activeProps={{ className: 'nav-mobile-tab active' }}
                onClick={() => setMenuOpen(false)}
              >
                {t.label}
              </Link>
            ))}
          </div>
        )}

        <div>
          <Outlet />
        </div>

        <div className={`toast${toast.visible ? ' show' : ''}`}>{toast.msg}</div>

        <InstallPrompt />

        <Footer version={version} year={new Date().getFullYear()} />
      </div>
    </ToastContext.Provider>
  );
}
